import { defineComponent as _defineComponent } from 'vue'
import {useAirDropStore} from "@/store/airDrop.store";
import {computed, onBeforeMount, onMounted, onUnmounted, ref, watch} from "vue";
import {useUserStore} from "@/store/user.store";
import {CampainStatus} from "@/models/airdrop/airdrop";
import {Campaign, Mission, MissionTypeSt} from "@/models/store/airdrop";
import PercentageBar from "@/components/commons/PercentageBar.vue";
import ClaimInfo from "@/components/airdrop/dropComponents/ClaimInfo.vue";
import CoinAmount from "@/components/commons/CoinAmount.vue";
import router from "@/router";
import ClaimingOptionsPopup from "@/components/airdrop/ClaimingOptionsPopup.vue";
import {useI18n} from "vue-i18n";
import LoginPopUp from "@/components/layout/loginPopup/LoginPopUp.vue";
import {ChevronDown, ChevronUp} from "lucide-vue-next";
import dataService from "@/services/data.service";
import Dialog from 'primevue/dialog';
import DateCommon from "@/components/commons/DateCommon.vue";
import {useToast} from "vue-toastification";
import {formatBigNumberLocalized, reduceBigNumberLocalized} from "@/utils/locale-number-formatter";
import {BigDecimal} from "@/models/store/big.decimal";
import {BigIntWrapper, Coin, DecCoin} from "@/models/store/common";
import {useConfigurationStore} from "@/store/configuration.store";


export default /*#__PURE__*/_defineComponent({
  __name: 'ClaimAirdrop',
  setup(__props, { expose: __expose }) {
  __expose();

let isFinal = false;
let currentClaimIsInitial = false;
let intervalId = 0;

const loginPopupStatus = ref<boolean>(false);
const sharePopupStatus = ref<boolean>(false);

const claimingProcessStarted = ref<boolean>(false);
const activeCampaign = ref<Campaign>();
const updateComponent = ref(false);

const selectedMission = ref<Mission>();
const selectedCampaign = ref<Campaign>();

const socialMediaMessage = ref<string>('');

const i18n = useI18n();
const airDropStore = useAirDropStore();

const userLoggedIn = computed(() => {
  return useUserStore().getAccount.address != '';
});

onBeforeMount(()=>{
  intervalId = window.setInterval(() => { updateComponent.value = !updateComponent.value;}, 1000);
});

onMounted(() => {
  dataService.onClaimAirdropSelected();
});

onUnmounted(() => {
  window.clearInterval(intervalId);
  dataService.onClaimAirdropUnselected();
});

watch(userLoggedIn, () => {
  console.log("watch: userLoggedIn - CHANGED");
  claimingProcessStarted.value = false;
  sharePopupStatus.value = false;
  loginPopupStatus.value = false;
});



function onClickedShareButton(campaignRecord: Campaign, mission: Mission) {
  generateSocialMediaMessage(campaignRecord, mission);
  sharePopupStatus.value = true;
}

function setActiveCampaign(campaign: Campaign) {
  if (campaign !== activeCampaign.value) {
    activeCampaign.value = campaign;
  } else {
    activeCampaign.value = undefined;
  }
}

function calculateMissions(campaign: Campaign) {
  let total = 0;
  campaign.missions.forEach((el) => {
    if (el.claimed) {
      total += Number(el.weight);
    }
  });
  return total;
}

function getAmountOfClaimedMissions(campaign: Campaign) {
  let total = 0;
  campaign.missions.forEach((el) => {
    if (el.claimed) {
      total++;
    }
  });
  return total;
}

function checkCampaignStatus(startTime: number | string, endTime: number | string) {
  if (new Date(startTime).getTime() < new Date(Date.now()).getTime() && new Date(endTime).getTime() > new Date(Date.now()).getTime()) {
    return CampainStatus.Now;
  } else if (new Date(startTime).getTime() > new Date(Date.now()).getTime()) {
    return CampainStatus.Future;
  } else {
    return CampainStatus.Past;
  }
}

function calculateProgress(startTime: number | string, endTime: number | string) {
  if (new Date(startTime).getTime() < Date.now() && new Date(endTime).getTime() > Date.now()) {
    const startEndDiff = new Date(endTime).getTime() - new Date(startTime).getTime();
    const difference = Date.now() - new Date(endTime).getTime();
    return Math.abs(100 - ((Math.abs(difference) / Math.abs(startEndDiff)) * 100));
  } else {
    return null;
  }
}

function getTextForTimeColumn(campaign: Campaign) {
  const res = checkCampaignStatus(campaign.start_time, campaign.end_time);
  switch (res) {
    case CampainStatus.Past:
      return i18n.t('AIRDROP.CAMPAIGN_PASSED');
    case CampainStatus.Now:
      return i18n.t('AIRDROP.CAMPAIGN_END');
    case CampainStatus.Future:
      if (campaign.enabled) {
        return i18n.t('AIRDROP.CAMPAIGN_START');
      } else {
        return i18n.t('AIRDROP.CAMPAIGN_START_SOON');
      }
  }
}

function calculateTimeToPass(campaign: Campaign) {
  const startDate = campaign.start_time;
  const endDate = campaign.end_time;
  if (new Date(startDate).getTime() < new Date(Date.now()).getTime() && new Date(endDate).getTime() > new Date(Date.now()).getTime()) {
    const now = new Date(Date.now());
    const difference = new Date(endDate).getTime() - now.getTime();
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);
    return `${days}D ${hours}H ${minutes}M ${seconds}S`;
  } else if (campaign.enabled){
    const now = new Date(Date.now());
    const difference = new Date(startDate).getTime() - now.getTime();
    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);
    return `${days}D ${hours}H ${minutes}M ${seconds}S`;
  // }else if(new Date(startDate).getTime() > new Date(Date.now()).getTime()) {
  } else if(!campaign.enabled) {
    return i18n.t('AIRDROP.SOON');
  } else {
    return '';
  }
}

function getTextForMissionsBtn(mission: Mission, type: MissionTypeSt) {
  let text;
  switch (type) {
    case MissionTypeSt.INITIAL_CLAIM:
      text = i18n.t('AIRDROP.CLAIM');
      break;
    case MissionTypeSt.DELEGATE:
      text = i18n.t('AIRDROP.DELEGATE');
      break;
    case MissionTypeSt.VOTE:
      text = i18n.t('AIRDROP.VOTE');
      break;
    case MissionTypeSt.CLAIM:
      text = i18n.t('AIRDROP.CLAIM');
      break;
    case MissionTypeSt.TO_DEFINE:
      text = i18n.t('AIRDROP.TO_DEFINE');
      break;
  }
  if (mission.completed && !mission.claimed) {
    text = i18n.t('AIRDROP.CLAIM');
  }
  return text;
}

function redirectMission(campaign: Campaign, mission: Mission, type: MissionTypeSt) {
  selectedCampaign.value = campaign;
  selectedMission.value = mission;
  isFinal = (campaign.missions.length - getAmountOfClaimedMissions(campaign) === 1);

  if (mission.mission_type === MissionTypeSt.INITIAL_CLAIM) {
    claimingProcessStarted.value = true;
    currentClaimIsInitial = true;
  } else if (mission.mission_type === MissionTypeSt.CLAIM) {
    currentClaimIsInitial = false;
    dataService.onClaimOtherAirdrop(campaign, mission, onSuccessClaim);
  } else {
    currentClaimIsInitial = false;
    if (mission.completed && !mission.claimed) {
      dataService.onClaimOtherAirdrop(campaign, mission, onSuccessClaim);
    } else {
      switch (type) {
        case MissionTypeSt.DELEGATE:
          router.push('staking');
          break;
        case MissionTypeSt.VOTE:
          router.push('governance');
          break;
      }
    }
  }
}


function isDisabled(campaignRec: Campaign, mission: Mission) {
  if (!campaignRec.enabled) {
    return false;
  } else if (checkCampaignStatus(campaignRec.start_time, campaignRec.end_time) !== CampainStatus.Now) {
    return false;
  } else if (!isInitialMissionClaimed(campaignRec) && mission.mission_type !== MissionTypeSt.INITIAL_CLAIM) {
    return false;
  } else if (mission.claimed || mission.mission_type === MissionTypeSt.TO_DEFINE) {
    return false;
  }
  return true;
}

function isInitialMissionClaimed(campaign: Campaign) {
  const initialMission = campaign.missions.find((mission) => {
    return mission.mission_type === 'INITIAL_CLAIM';
  });
  return initialMission?.claimed;
}

function generateSocialMediaMessage(campaign: Campaign, mission?: Mission) {
  //TODO:  quick FIX !!! /// to refactor!!!
  if(campaign.missions.length == 1){
    isFinal = true;
  }
  /////////////////////////
  if (isFinal) {
    // const campaignAmount = retrieveConvertedAmount(campaign.amount);
    socialMediaMessage.value = i18n.t('AIRDROP.SHARE_MESSAGE_CAMPAIGN_COMPLETED');
    isFinal = false;
  } else {
    const missionAmount = transformToExpView(Number(mission?.weight) / 1000000);
    socialMediaMessage.value = i18n.t('AIRDROP.SHARE_MESSAGE_MISSION_COMPLETED', {campaignName: campaign?.name, missionName:mission?.name, missionAmount:missionAmount});
  }
}

function transformToExpView(amount: number | bigint) {
  return formatBigNumberLocalized(typeof amount === 'bigint' ? amount.toString() : amount.toFixed(6));
}

// function retrieveConvertedAmount(amount: Coin ): string {
//   const amountStr = useConfigurationStore().config.getConvertedAmount(amount.amount, amount.denom).toFixed(6);
//   return formatBigNumberLocalized(amountStr);
// }

function handleMissionCompleted(campaign: Campaign, mission: Mission) {
  generateSocialMediaMessage(campaign, mission);
  sharePopupStatus.value = true;
}

function claim(address: string) {
  console.log("claim:", address);
  if (selectedCampaign.value && selectedMission.value) {
    if (currentClaimIsInitial) {
      dataService.onClaimInitialAirdrop(selectedCampaign.value, selectedMission.value, address, onSuccessClaim);
    } else {
      dataService.onClaimOtherAirdrop(selectedCampaign.value, selectedMission.value, onSuccessClaim);
    }
  } else {
    console.warn("claim: selectedCampaign or selectedMission not provided!!");
  }
}

function onSuccessClaim(campaign: Campaign, mission: Mission){
  claimingProcessStarted.value = false;
  handleMissionCompleted(campaign, mission);
}

function hideTotalDistribution(campaign: Campaign){
  const airDropTotal = airDropStore.getAirDropTotal;
  const campaignConfig = airDropTotal.campaignAllocations?.find((e)=> e.name == campaign.name);
  return campaignConfig ? campaignConfig.hide_total_distribution : false;
}

function encodeUrlPart(toEncode: string){
  return encodeURIComponent(toEncode);
}


const __returned__ = { get isFinal() { return isFinal }, set isFinal(v) { isFinal = v }, get currentClaimIsInitial() { return currentClaimIsInitial }, set currentClaimIsInitial(v) { currentClaimIsInitial = v }, get intervalId() { return intervalId }, set intervalId(v) { intervalId = v }, loginPopupStatus, sharePopupStatus, claimingProcessStarted, activeCampaign, updateComponent, selectedMission, selectedCampaign, socialMediaMessage, i18n, airDropStore, userLoggedIn, onClickedShareButton, setActiveCampaign, calculateMissions, getAmountOfClaimedMissions, checkCampaignStatus, calculateProgress, getTextForTimeColumn, calculateTimeToPass, getTextForMissionsBtn, redirectMission, isDisabled, isInitialMissionClaimed, generateSocialMediaMessage, transformToExpView, handleMissionCompleted, claim, onSuccessClaim, hideTotalDistribution, encodeUrlPart, get useAirDropStore() { return useAirDropStore }, get useUserStore() { return useUserStore }, get CampainStatus() { return CampainStatus }, PercentageBar, ClaimInfo, CoinAmount, ClaimingOptionsPopup, LoginPopUp, get ChevronDown() { return ChevronDown }, get ChevronUp() { return ChevronUp }, get Dialog() { return Dialog }, DateCommon }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})